import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Avatar,
    Box,
    Button,
    Chip,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    Call as CallIcon,
    CallEnd as CallEndIcon,
} from '@mui/icons-material';
import { useSIPProvider } from 'react-sipjs';
import type { SIPProviderOptions } from 'react-sipjs/dist/type';
import { SessionState } from 'sip.js';
import { useDispatch, useSelector } from 'react-redux';
import * as callActions from 'App/store/actions/call.action';
import NotificationTone from 'App/common/notification/notification.mp3';
import CallSessionItem from './CallSessionItem';

interface VICIDialCallComponentProps {
    call?: any;
    user?: any;
    sipConfig?: SIPProviderOptions;
}

const VICIDialCallComponent: React.FC<VICIDialCallComponentProps> = ({ call, user, sipConfig }) => {
    const { sessionManager, connectAndRegister, connectStatus, registerStatus, sessions } = useSIPProvider();
    const dispatch = useDispatch();
    const viciSession = useSelector((state: any) => state.call?.viciSession);
    const receiveAckMsg = useSelector((state: any) => state.call?.receiveAckMsg);
    const receiveViciAckMsg = useSelector((state: any) => state.call?.receiveViciAckMsg);
    const socApiBase = (process.env.REACT_APP_SOC_API_URL || '').replace(/\/$/, '');

    const [username, setUsername] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [targetNumber, setTargetNumber] = useState<string>('');
    const [sipError, setSipError] = useState<string>('');
    const [lineError, setLineError] = useState<string>('');
    const [lineMessage, setLineMessage] = useState<string>('');
    const [lineStatus, setLineStatus] = useState<'idle' | 'waiting' | 'allocating' | 'ready' | 'error'>('idle');
    const [isRegistered, setIsRegistered] = useState(false);
    const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
    const [defaultCredentials, setDefaultCredentials] = useState<{ username: string; password: string } | null>(null);
    const [isAwaitingToConnection, setIsAwaitingToConnection] = useState(false);
    const [hasAcceptedCall, setHasAcceptedCall] = useState(false);
    const [ackSent, setAckSent] = useState(false);
    const [hasAudioPermission, setHasAudioPermission] = useState(false);
    const [requestingPermission, setRequestingPermission] = useState(false);
    const [permissionError, setPermissionError] = useState<string>('');

    const releaseInProgressRef = useRef(false);
    const callingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const autoCallInitiatedRef = useRef(false);
    const ringtoneAudioRef = useRef<HTMLAudioElement | null>(null);
    const setupInProgressRef = useRef(false);
    const viciSessionIdRef = useRef<string | null>(null);

    const callFromId = call?.from?.id !== undefined && call?.from?.id !== null ? String(call.from.id) : null;
    const callToId = call?.to?.id !== undefined && call?.to?.id !== null ? String(call.to.id) : null;
    const currentUserId = user?.id !== undefined && user?.id !== null ? String(user.id) : null;
    const isInitiator = !!callFromId && !!currentUserId && callFromId === currentUserId;
    const isReceiver = !!callToId && !!currentUserId && callToId === currentUserId;

    const requestAudioPermission = useCallback(async () => {
        if (hasAudioPermission || requestingPermission) {
            return;
        }
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            setHasAudioPermission(true);
            setPermissionError('');
            return;
        }
        try {
            setRequestingPermission(true);
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            stream.getTracks().forEach((track) => track.stop());
            setHasAudioPermission(true);
            setPermissionError('');
        } catch (err: any) {
            console.error('Microphone permission denied:', err);
            setHasAudioPermission(false);
            setPermissionError('Microphone access is required for the call. Please allow access and try again.');
        } finally {
            setRequestingPermission(false);
        }
    }, [hasAudioPermission, requestingPermission]);

    useEffect(() => {
        requestAudioPermission();
    }, [requestAudioPermission]);

    useEffect(() => {
        ringtoneAudioRef.current = new Audio(NotificationTone);
        ringtoneAudioRef.current.loop = true;
        ringtoneAudioRef.current.preload = 'auto';
        return () => {
            if (ringtoneAudioRef.current) {
                ringtoneAudioRef.current.pause();
                ringtoneAudioRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (!ringtoneAudioRef.current) {
            return;
        }
        const shouldRing =
            isReceiver &&
            !!viciSession &&
            !hasAcceptedCall &&
            !activeSessionId &&
            lineStatus === 'ready';

        if (shouldRing) {
            ringtoneAudioRef.current.currentTime = 0;
            const playPromise = ringtoneAudioRef.current.play();
            if (playPromise) {
                playPromise.catch((err) => {
                    console.warn('Unable to play incoming call ringtone:', err);
                });
            }
        } else {
            ringtoneAudioRef.current.pause();
            ringtoneAudioRef.current.currentTime = 0;
        }
    }, [isReceiver, viciSession, hasAcceptedCall, activeSessionId, lineStatus]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const usernameFromQuery = params.get('username');
        const passwordFromQuery = params.get('password');

        if (usernameFromQuery && passwordFromQuery) {
            setDefaultCredentials({ username: usernameFromQuery, password: passwordFromQuery });
        } else if (user?.mobile) {
            setDefaultCredentials({ username: user.mobile, password: 'iConnect786' });
        } else {
            setDefaultCredentials(null);
        }
    }, [user]);

    useEffect(() => {
        if (viciSession) {
            if (isInitiator && viciSession.to_number?.username) {
                setTargetNumber(viciSession.to_number.username);
                return;
            }
            if (isReceiver && viciSession.from_number?.username) {
                setTargetNumber(viciSession.from_number.username);
                return;
            }
        }
        if (call) {
            let targetExt = '';
            if (user?.type === 'rider') {
                targetExt = call.to?.mobile || '1000';
            } else if (user?.type === 'driver') {
                targetExt = call.from?.mobile || '1000';
            } else {
                targetExt = call.to?.mobile || '1000';
            }
            setTargetNumber(targetExt);
        } else {
            setTargetNumber('');
        }
    }, [viciSession, call, user, isInitiator, isReceiver]);

    useEffect(() => {
        if (viciSession) {
            if (isInitiator && viciSession.from_number?.username) {
                setUsername(viciSession.from_number.username);
                setPassword(viciSession.from_number.password || '');
            } else if (isReceiver && viciSession.to_number?.username) {
                setUsername(viciSession.to_number.username);
                setPassword(viciSession.to_number.password || '');
            }
        } else if (defaultCredentials) {
            setUsername(defaultCredentials.username);
            setPassword(defaultCredentials.password);
        } else {
            setUsername('');
            setPassword('');
        }
    }, [viciSession, defaultCredentials, isInitiator, isReceiver]);

    useEffect(() => {
        if (!call || call.type !== 'vici' || !callFromId || !callToId) {
            setLineStatus('idle');
            setLineMessage('');
            setLineError('');
            return;
        }

        if (!socApiBase) {
            setLineStatus('error');
            setLineError('Call service is unavailable');
            setLineMessage('');
            return;
        }

        if (
            viciSession &&
            (viciSession.from_id !== callFromId || viciSession.to_id !== callToId)
        ) {
            dispatch(callActions.clearViciSessionState());
        }

        if (
            viciSession &&
            viciSession.from_id === callFromId &&
            viciSession.to_id === callToId
        ) {
            setLineStatus('ready');
            setLineMessage(isReceiver ? 'Incoming call...' : '');
            setLineError('');
            return;
        }

        if (!isInitiator) {
            setLineStatus('waiting');
            setLineMessage('Waiting for line assignment...');
            setLineError('');
            return;
        }

        if (setupInProgressRef.current) {
            return;
        }

        let cancelled = false;
        const reserveLine = async () => {
            setupInProgressRef.current = true;
            setLineStatus('allocating');
            setLineMessage('Reserving a call line...');
            setLineError('');
            try {
                const response = await fetch(`${socApiBase}/call/setup`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        from_id: call.from.id,
                        to_id: call.to.id,
                        from_name: call.from.name,
                        to_name: call.to.name,
                    }),
                });
                const payload = await response.json().catch(() => ({}));
                if (!response.ok) {
                    throw new Error(payload?.msg || 'Unable to setup call');
                }
                if (!cancelled) {
                    dispatch(callActions.setViciSession(payload.data));
                    setLineStatus('ready');
                    setLineMessage('');
                    setLineError('');
                }
            } catch (err: any) {
                if (cancelled) {
                    return;
                }
                const message = err?.message || 'Unable to setup call';
                setLineStatus('error');
                setLineError(message);
                setLineMessage('');
                dispatch(callActions.setEndCall());
            } finally {
                setupInProgressRef.current = false;
            }
        };

        reserveLine();

        return () => {
            cancelled = true;
        };
    }, [call, callFromId, callToId, isInitiator, isReceiver, socApiBase, viciSession, dispatch]);

    useEffect(() => {
        if (!viciSession) {
            setLineStatus((prev) => (prev === 'ready' ? 'idle' : prev));
            setLineMessage('');
        }
    }, [viciSession]);

    useEffect(() => {
        if (username && password && lineStatus === 'ready' && !isRegistered && hasAudioPermission) {
            connectAndRegister({
                username,
                password,
            });
        }
    }, [username, password, lineStatus, isRegistered, hasAudioPermission, connectAndRegister, connectStatus, registerStatus]);

    useEffect(() => {
        setIsRegistered(false);
    }, [username, password]);

    useEffect(() => {
        if (registerStatus === 'REGISTERED') {
            setIsRegistered(true);
            setSipError('');
        }
    }, [registerStatus]);

    useEffect(() => {
        viciSessionIdRef.current = viciSession?.session_id || null;
    }, [viciSession?.session_id]);

    const sendDisconnectRequest = useCallback(
        async (sessionId: string | null, options: { keepAlive?: boolean; useBeacon?: boolean } = {}) => {
            if (!socApiBase || !sessionId) {
                return;
            }

            const url = `${socApiBase}/call/disconnect`;
            const payload = JSON.stringify({ session_id: sessionId });

            if (options.useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
                const blob = new Blob([payload], { type: 'application/json' });
                const queued = navigator.sendBeacon(url, blob);
                if (queued) {
                    return;
                }
            }

            await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: payload,
                keepalive: options.keepAlive ?? options.useBeacon ?? false,
            });
        },
        [socApiBase],
    );

    const releaseNumbers = useCallback(async () => {
        const sessionId = viciSessionIdRef.current;
        if (!sessionId) {
            dispatch(callActions.clearViciSessionState());
            return;
        }
        if (releaseInProgressRef.current) {
            return;
        }
        releaseInProgressRef.current = true;
        try {
            await sendDisconnectRequest(sessionId);
        } catch (err) {
            console.error('Failed to release VICIDial line:', err);
        } finally {
            releaseInProgressRef.current = false;
            dispatch(callActions.clearViciSessionState());
            setLineStatus('idle');
            setLineMessage('');
            setLineError('');
        }
    }, [dispatch, sendDisconnectRequest]);

    const clearCallingTimeout = useCallback(() => {
        if (callingTimeoutRef.current) {
            clearTimeout(callingTimeoutRef.current);
            callingTimeoutRef.current = null;
        }
    }, []);

    const handleCallingTimeout = useCallback(() => {
        setLineError('No response from the other participant. Disconnecting...');
        setLineMessage('');
        setSipError('Call timed out while waiting for the other participant.');
        setIsAwaitingToConnection(false);
        clearCallingTimeout();
        releaseNumbers();
    }, [clearCallingTimeout, releaseNumbers]);

    const startCallingTimeout = useCallback(() => {
        clearCallingTimeout();
        callingTimeoutRef.current = setTimeout(handleCallingTimeout, 60000);
    }, [clearCallingTimeout, handleCallingTimeout]);

    useEffect(() => {
        const sessionIds = Object.keys(sessions);
        if (sessionIds.length > 0) {
            setActiveSessionId(sessionIds[0]);
        } else {
            setActiveSessionId(null);
        }
    }, [sessions]);

    useEffect(() => {
        if (!activeSessionId) {
            if (isAwaitingToConnection) {
                setIsAwaitingToConnection(false);
                clearCallingTimeout();
            }
            return;
        }

        const activeSession = sessions[activeSessionId] as any;
        const sessionState: SessionState | undefined =
            activeSession?.state ?? activeSession?.session?.state;

        if (sessionState === SessionState.Established && !isAwaitingToConnection) {
            setIsAwaitingToConnection(true);
            setLineError('');
            setLineMessage('Calling...');
            startCallingTimeout();
        } else if (sessionState && sessionState !== SessionState.Established && isAwaitingToConnection) {
            setIsAwaitingToConnection(false);
            clearCallingTimeout();
        }
    }, [activeSessionId, sessions, isAwaitingToConnection, startCallingTimeout, clearCallingTimeout]);

    useEffect(() => {
        if (!receiveAckMsg) {
            return;
        }

        setLineMessage(receiveAckMsg);

        const normalizedMsg = receiveAckMsg.trim().toLowerCase();
        if ((normalizedMsg.includes('ring') || normalizedMsg.includes('connect')) && isAwaitingToConnection) {
            setIsAwaitingToConnection(false);
            clearCallingTimeout();
        }
    }, [receiveAckMsg, isAwaitingToConnection, clearCallingTimeout]);

    useEffect(() => {
        return () => {
            releaseNumbers();
        };
    }, [releaseNumbers]);

    useEffect(() => {
        return () => {
            clearCallingTimeout();
        };
    }, [clearCallingTimeout]);

    useEffect(() => {
        const handleBeforeUnload = () => {
            const sessionId = viciSessionIdRef.current;
            if (!sessionId) {
                return;
            }
            sendDisconnectRequest(sessionId, { useBeacon: true, keepAlive: true }).catch((err) =>
                console.error('Failed to send unload disconnect request:', err),
            );
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [sendDisconnectRequest]);

    const makeCall = useCallback(async () => {
        if (lineStatus !== 'ready' || !viciSession?.session_id) {
            setSipError(lineError || 'Call line is not ready yet. Please wait.');
            return;
        }

        if (!sessionManager) {
            setSipError('Session manager not available. Please check your connection.');
            return;
        }

        if (!targetNumber) {
            setSipError('Please enter a target number');
            return;
        }

        if (registerStatus !== 'REGISTERED') {
            setSipError('Not registered. Please wait for registration to complete.');
            return;
        }

        const sipDomain =
            sessionManager?.userAgent?.configuration?.uri?.host ||
            sipConfig?.domain ||
            process.env.REACT_APP_VICIDIAL_DOMAIN;

        if (!sipDomain) {
            setSipError('Unable to determine SIP domain. Please check your VICIDial configuration.');
            return;
        }

        try {
            setSipError('');
            const cleanNumber = targetNumber.replace(/[^0-9]/g, '');
            const target = `sip:${cleanNumber}@${sipDomain}`;

            await sessionManager.call(target, {
                sessionDescriptionHandlerOptions: {
                    constraints: {
                        audio: true,
                        video: false
                    }
                }
            });
        } catch (err: any) {
            setSipError(`Failed to make call: ${err.message || 'Unknown error'}`);
        }
    }, [lineStatus, viciSession?.session_id, sessionManager, targetNumber, registerStatus, lineError, sipConfig?.domain]);

    const sendAcknowledgement = useCallback(() => {
        if (!viciSession || ackSent) {
            return;
        }

        const ackPayload = {
            session_id: viciSession.session_id,
            from_id: viciSession.from_id,
            to_id: viciSession.to_id,
            status: 'ready',
            message: 'Receiver is ready for call',
        };
        dispatch(callActions.setViciAcknowledge(ackPayload));
        setAckSent(true);
        setHasAcceptedCall(true);
        setLineMessage('Connecting...');
    }, [viciSession, ackSent, dispatch]);

    const handleAcceptCall = useCallback(() => {
        sendAcknowledgement();
    }, [sendAcknowledgement]);

    const handleDeclineCall = useCallback(() => {
        releaseNumbers();
        dispatch(callActions.setEndCall());
    }, [releaseNumbers, dispatch]);

    useEffect(() => {
        const canInitiateCall = isRegistered &&
            lineStatus === 'ready' &&
            targetNumber &&
            sessionManager &&
            !activeSessionId &&
            !autoCallInitiatedRef.current;

        if (!canInitiateCall) {
            return;
        }

        if (isInitiator) {
            if (receiveViciAckMsg) {
                autoCallInitiatedRef.current = true;
                makeCall();
            } else {
                setLineMessage('Waiting for other party to accept...');
            }
        } else if (isReceiver && hasAcceptedCall) {
            setLineMessage('Waiting for caller to connect...');
        }
    }, [isRegistered, lineStatus, targetNumber, sessionManager, activeSessionId, makeCall, isInitiator, isReceiver, receiveViciAckMsg, hasAcceptedCall]);

    const handleHangup = useCallback(() => {
        releaseNumbers();
        setActiveSessionId(null);
    }, [releaseNumbers]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                backgroundColor: '#f5f5f5',
                p: 3,
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    p: 4,
                    borderRadius: 3,
                    textAlign: 'center',
                    maxWidth: 400,
                    width: '100%',
                }}
            >
                <Avatar
                    sx={{
                        width: 120,
                        height: 120,
                        mx: 'auto',
                        mb: 2,
                        bgcolor: isRegistered ? '#4CAF50' : '#42a5f5',
                    }}
                >
                    <CallIcon sx={{ fontSize: 60 }} />
                </Avatar>

                <Typography variant="h5" gutterBottom>
                    VICIDial
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                    User: {username}
                </Typography>

                {!hasAudioPermission && (
                    <Box mb={2}>
                        <Typography variant="body2" color="error" mb={1}>
                            Microphone access is required to continue.
                        </Typography>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={requestAudioPermission}
                            disabled={requestingPermission}
                            fullWidth
                        >
                            {requestingPermission ? 'Requesting Permission...' : 'Enable Microphone'}
                        </Button>
                        {permissionError && (
                            <Typography variant="caption" color="error" display="block" mt={1}>
                                {permissionError}
                            </Typography>
                        )}
                    </Box>
                )}

                <Stack direction="row" spacing={1} justifyContent="center" mb={2}>
                    <Chip
                        label={connectStatus === 'CONNECTED' ? 'Connected' : connectStatus === 'DISCONNECTED' ? 'Disconnected' : 'Connecting...'}
                        color={connectStatus === 'CONNECTED' ? 'success' : connectStatus === 'DISCONNECTED' ? 'error' : 'warning'}
                        size="small"
                    />
                    <Chip
                        label={registerStatus === 'REGISTERED' ? 'Registered' : 'Unregistered'}
                        color={registerStatus === 'REGISTERED' ? 'success' : 'default'}
                        size="small"
                    />
                </Stack>

                {lineMessage && (
                    <Typography variant="body2" color="text.secondary" mb={1}>
                        {lineMessage}
                    </Typography>
                )}

                {lineError && (
                    <Typography variant="body2" color="error" mb={1}>
                        {lineError}
                    </Typography>
                )}

                {sipError && (
                    <Typography variant="body2" color="error" mb={2}>
                        {sipError}
                    </Typography>
                )}

                {!hasAudioPermission && (
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Allow microphone access above to start the call.
                    </Typography>
                )}

                {activeSessionId && (
                    <CallSessionItem sessionId={activeSessionId} onHangup={handleHangup} />
                )}

                {!activeSessionId && isReceiver && !hasAcceptedCall && viciSession && (
                    <Box>
                        <Typography variant="h6" color="warning.main" mb={2}>
                            Incoming Call
                        </Typography>
                        <Typography variant="body1" color="text.secondary" mb={2}>
                            Call from: {call?.from?.name || 'Unknown'}
                        </Typography>
                        <Stack direction="row" spacing={3} justifyContent="center" mt={3}>
                            <IconButton
                                onClick={handleAcceptCall}
                                sx={{
                                    backgroundColor: '#4CAF50',
                                    color: 'white',
                                    width: 70,
                                    height: 70,
                                    '&:hover': {
                                        backgroundColor: '#45a049',
                                    },
                                }}
                            >
                                <CallIcon sx={{ fontSize: 35 }} />
                            </IconButton>
                            <IconButton
                                onClick={handleDeclineCall}
                                sx={{
                                    backgroundColor: '#F44336',
                                    color: 'white',
                                    width: 70,
                                    height: 70,
                                    '&:hover': {
                                        backgroundColor: '#da190b',
                                    },
                                }}
                            >
                                <CallEndIcon sx={{ fontSize: 35 }} />
                            </IconButton>
                        </Stack>
                    </Box>
                )}

                {!activeSessionId && isReceiver && hasAcceptedCall && (
                    <Box>
                        <Typography variant="h6" color="primary" mb={2}>
                            Connecting...
                        </Typography>
                        <IconButton
                            onClick={handleDeclineCall}
                            sx={{
                                backgroundColor: '#F44336',
                                color: 'white',
                                width: 60,
                                height: 60,
                                mt: 2,
                                '&:hover': {
                                    backgroundColor: '#da190b',
                                },
                            }}
                        >
                            <CallEndIcon />
                        </IconButton>
                    </Box>
                )}

                {!activeSessionId && isInitiator && !receiveViciAckMsg && lineStatus === 'ready' && (
                    <Box>
                        <Typography variant="h6" color="primary" mb={2}>
                            Calling...
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            Waiting for {call?.to?.name || 'other party'} to accept
                        </Typography>
                        <IconButton
                            onClick={handleHangup}
                            sx={{
                                backgroundColor: '#F44336',
                                color: 'white',
                                width: 60,
                                height: 60,
                                mt: 2,
                                '&:hover': {
                                    backgroundColor: '#da190b',
                                },
                            }}
                        >
                            <CallEndIcon />
                        </IconButton>
                    </Box>
                )}

                {!activeSessionId && isInitiator && receiveViciAckMsg && (
                    <Box>
                        <Typography variant="h6" color="success.main" mb={2}>
                            Connecting...
                        </Typography>
                        <IconButton
                            onClick={handleHangup}
                            sx={{
                                backgroundColor: '#F44336',
                                color: 'white',
                                width: 60,
                                height: 60,
                                mt: 2,
                                '&:hover': {
                                    backgroundColor: '#da190b',
                                },
                            }}
                        >
                            <CallEndIcon />
                        </IconButton>
                    </Box>
                )}

                {!activeSessionId && !isReceiver && !isInitiator && (
                    <>
                        <TextField
                            fullWidth
                            label="Target Number"
                            value={targetNumber}
                            onChange={(e) => setTargetNumber(e.target.value)}
                            margin="normal"
                            size="small"
                            disabled={registerStatus !== 'REGISTERED' || lineStatus !== 'ready' || !hasAudioPermission}
                        />

                        <Button
                            variant="contained"
                            color="success"
                            fullWidth
                            onClick={makeCall}
                            disabled={registerStatus !== 'REGISTERED' || !targetNumber || lineStatus !== 'ready' || !hasAudioPermission}
                            sx={{ mt: 2, mb: 2 }}
                            startIcon={<CallIcon />}
                        >
                            {registerStatus === 'REGISTERED'
                                ? lineStatus === 'ready'
                                    ? 'Make Call'
                                    : 'Preparing line...'
                                : 'Waiting for Registration...'}
                        </Button>
                    </>
                )}

                {Object.keys(sessions).length > 1 && (
                    <Box mt={2}>
                        <Typography variant="body2" color="text.secondary">
                            Active Sessions: {Object.keys(sessions).length}
                        </Typography>
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default VICIDialCallComponent;
